import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Loader2, ShoppingBag, Filter, X, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "../context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HerbEncyclopedia = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [herbs, setHerbs] = useState([]);
  const [filteredHerbs, setFilteredHerbs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBenefits, setSelectedBenefits] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("alphabetical");

  // Extract unique categories and benefits
  const categories = [...new Set(herbs.map(h => h.category))].sort();
  const allBenefits = [...new Set(herbs.flatMap(h => {
    if (!h.benefits) return [];
    const benefitsStr = typeof h.benefits === 'string' ? h.benefits : h.benefits.join(', ');
    return benefitsStr.split(',').map(b => b.trim()).filter(Boolean);
  }))].sort();

  useEffect(() => {
    let result = [...herbs];
    
    // Apply search filter
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      result = result.filter(herb => {
        const benefitsStr = herb.benefits ? 
          (typeof herb.benefits === 'string' ? herb.benefits : herb.benefits.join(', ')) : '';
        return herb.name.toLowerCase().includes(query) ||
          herb.scientific_name.toLowerCase().includes(query) ||
          herb.category.toLowerCase().includes(query) ||
          benefitsStr.toLowerCase().includes(query);
      });
    }
    
    // Apply category filter
    if (selectedCategories.length > 0) {
      result = result.filter(herb => selectedCategories.includes(herb.category));
    }
    
    // Apply benefits filter
    if (selectedBenefits.length > 0) {
      result = result.filter(herb => {
        if (!herb.benefits) return false;
        const benefitsStr = typeof herb.benefits === 'string' ? herb.benefits : herb.benefits.join(', ');
        const herbBenefits = benefitsStr.split(',').map(b => b.trim().toLowerCase());
        return selectedBenefits.some(benefit => 
          herbBenefits.some(hb => hb.includes(benefit.toLowerCase()))
        );
      });
    }
    
    // Apply sorting
    if (sortBy === "alphabetical") {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    setFilteredHerbs(result);
  }, [searchQuery, herbs, selectedCategories, selectedBenefits, sortBy]);

  const fetchHerbs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/herbs`);
      // Sort alphabetically by default
      const sortedHerbs = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setHerbs(sortedHerbs);
      setFilteredHerbs(sortedHerbs);
    } catch (error) {
      console.error('Error fetching herbs:', error);
      toast.error("Failed to load herbs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHerbs();
  }, [fetchHerbs]);

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleBenefit = (benefit) => {
    setSelectedBenefits(prev => 
      prev.includes(benefit)
        ? prev.filter(b => b !== benefit)
        : [...prev, benefit]
    );
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedBenefits([]);
    setSearchQuery("");
  };

  const activeFilterCount = selectedCategories.length + selectedBenefits.length;

  const handleShopAll = () => navigate("/store");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="encyclopedia-title">
            Herbal Encyclopedia
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mb-3" data-testid="encyclopedia-subtitle">
            Explore our comprehensive database of herbs with detailed information about traditionally associated benefits, uses, and safety precautions.
          </p>
          <p className="text-xs text-gray-400 max-w-2xl mx-auto mb-8" data-testid="encyclopedia-disclaimer">
            Educational information describing traditional uses only — not medical advice, diagnosis or treatment claims.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search herbs by name, category, or benefits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 rounded-full"
              data-testid="herb-search-input"
            />
          </div>

          {/* Shop CTA */}
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-full px-6 py-3">
            <ShoppingBag className="w-5 h-5 text-primary" />
            <span className="text-gray-700 font-medium">Looking to purchase herbs?</span>
            <Button
              onClick={handleShopAll}
              size="sm"
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="shop-cta-btn"
            >
              Visit Our Store
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="rounded-full"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>

            {activeFilterCount > 0 && (
              <Button
                onClick={clearAllFilters}
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-primary"
              >
                <X className="w-4 h-4 mr-1" />
                Clear all filters
              </Button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Category Filter */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                    Categories
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {categories.map(category => (
                      <label
                        key={category}
                        className="flex items-center p-2 hover:bg-white rounded-lg cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => toggleCategory(category)}
                          className="w-4 h-4 text-primary rounded focus:ring-primary"
                        />
                        <span className="ml-3 text-sm text-gray-700">{category}</span>
                        <span className="ml-auto text-xs text-gray-400">
                          {herbs.filter(h => h.category === category).length}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Benefits Filter */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                    Common Benefits
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {allBenefits.slice(0, 20).map(benefit => (
                      <label
                        key={benefit}
                        className="flex items-center p-2 hover:bg-white rounded-lg cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBenefits.includes(benefit)}
                          onChange={() => toggleBenefit(benefit)}
                          className="w-4 h-4 text-primary rounded focus:ring-primary"
                        />
                        <span className="ml-3 text-sm text-gray-700">{benefit}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {activeFilterCount > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedCategories.map(category => (
                <span
                  key={category}
                  className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium"
                >
                  {category}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedBenefits.map(benefit => (
                <span
                  key={benefit}
                  className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium"
                >
                  {benefit}
                  <button
                    onClick={() => toggleBenefit(benefit)}
                    className="hover:bg-emerald-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-600" data-testid="search-results-count">
            Showing {filteredHerbs.length} {filteredHerbs.length === 1 ? 'herb' : 'herbs'}
            {(searchQuery || activeFilterCount > 0) && ' (filtered)'}
          </p>
        </div>

        {/* Herbs Grid */}
        {filteredHerbs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No herbs found matching your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredHerbs.map((herb) => (
              <Link
                key={herb.id}
                to={`/herbs/${herb.id}`}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all card-hover-effect group"
                data-testid={`herb-card-${herb.id}`}
              >
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img
                    src={herb.image_url}
                    alt={herb.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <div className="mb-2">
                    <span className="inline-block px-2 py-1 bg-secondary text-primary text-xs font-medium rounded-full">
                      {herb.category}
                    </span>
                  </div>
                  <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-1">
                    {herb.name}
                  </h3>
                  <p className="font-mono text-xs text-gray-500 mb-2">
                    {herb.scientific_name}
                  </p>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {herb.description}
                  </p>
                  <div className="flex items-center justify-between">
                    {herb.price_gbp ? (
                      <span className="font-bold text-primary" data-testid={`herb-price-${herb.id}`}>£{herb.price_gbp.toFixed(2)}</span>
                    ) : <span />}
                    <button
                      onClick={(e) => { e.preventDefault(); addItem(herb.id); toast.success(`${herb.name} added to cart`); }}
                      data-testid={`herb-add-cart-${herb.id}`}
                      className="inline-flex items-center gap-1.5 bg-primary text-white text-xs font-semibold rounded-full px-3 py-1.5 hover:bg-primary/90 transition-colors"
                    >
                      <ShoppingBag className="w-3.5 h-3.5" /> Add to Cart
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HerbEncyclopedia;